// ════════════════════════════════════════════════════════════
//  ArtFx.jsx — THE ARTS' OWN LOOK: slashes, meteors, domes, columns
// ════════════════════════════════════════════════════════════
//  caP'n, 2026-10-20: "just the actual arts, visual fx, and arena".
//  Effects.jsx draws the shared language (sparks, rings, shots,
//  pillars, scars). This file adds the PRIMITIVES the Arts need to
//  each look like themselves — and `artSignatures.js` composes them
//  into one signature per Art shape, per self-Art family and per
//  being's Lyt Attack. This file only knows HOW to draw; the
//  signatures say WHAT.
//
//  The same laws as Effects.jsx: every primitive is a FIXED POOL
//  built once (nothing mounts mid-fight), ONE useFrame drives all of
//  them, no lights (every glow is additive or basic), and the time
//  scale (hitstop, pause, slow-mo) is honoured.
//
//  The bus (registered on mount, read by artSignatures.js):
//    fx.slash({x,y,z,yaw,pitch,roll,r,arc,width,color,life,sweep,tail,flip})
//        a crescent that SWEEPS (a head running along the arc, a tail behind)
//    fx.rock({from:[x,y,z],to:[x,y,z],r,color,life,trail,onEnd})   a meteor
//    fx.dome({x,z,y,r0,r1,color,life,opacity,dark})   a shell; dark = ink, not light
//    fx.column({x,z,r,h,color,life})                  light from the sky
//    fx.disc({x,z,y,r0,r1,color,life,tex,spin,face})  flat glyph: swirl · hex · fracture (or a texture); face = turn to the viewer
//    fx.seal({art,x,z,r0,r1,color,life,spin})         an Art's SEAL (data/seals.js) — the only way a seal is drawn
//    fx.blot({x,z,r0,r1,life,opacity})                a shadow on the floor
//    fx.shard({x,y,z,vx,vy,vz,len,color,life,spin})  a crystal splinter
//    fx.halo({x,y,z,r0,r1,vy,color,life,tilt})        a thin ring in the air
//    fx.die({x,z,life,face}) · fx.coin({x,z,y,life})  the gambler's props
//    fx.after(seconds, fn)                            a beat later (scaled time)
//
//  ── POOL SIZES ARE DIALS ─────────────────────────────────────
// ════════════════════════════════════════════════════════════
import { useEffect, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { canvasTex, mulberry32 } from "../ieBeingModels.jsx";
import { glowTex, sealBodyTex, sealFourTex } from "./fxTex.js";
import { sealOf } from "../data/seals.js";

export const POOLS = { slash: 40, rock: 24, die: 4, coin: 4, dome: 6, column: 6, disc: 26, blot: 6, shard: 36, halo: 12 };
const N_TIMERS = 64;
const ADD = THREE.AdditiveBlending;

// ── painted textures (cached, seeded) ───────────────────────
const columnTex = () => canvasTex("art_column_v1", 64, 256, (g, w, h) => {
  const img = g.createImageData(w, h);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const u = x / (w - 1), v = y / (h - 1);                 // v 0 = top of the canvas = top of the column
    const across = Math.pow(Math.sin(Math.PI * u), 2.2);    // bright where the cylinder faces you
    const up = Math.pow(v, 1.6);                            // brightest at the floor, gone at the top
    const a = across * up;
    const i = (y * w + x) * 4;
    img.data[i] = img.data[i + 1] = img.data[i + 2] = 255; img.data[i + 3] = Math.round(255 * Math.min(1, a * 1.2));
  }
  g.putImageData(img, 0, 0);
});
const swirlTex = () => canvasTex("art_swirl_v1", 256, 256, (g, w) => {
  const c = w / 2; g.translate(c, c);
  for (let arm = 0; arm < 4; arm++) {
    g.beginPath();
    for (let k = 0; k <= 80; k++) {
      const t = k / 80, a = arm * Math.PI / 2 + t * 5.2, r = 6 + t * (c - 10);
      const x = Math.cos(a) * r, y = Math.sin(a) * r;
      k ? g.lineTo(x, y) : g.moveTo(x, y);
    }
    g.strokeStyle = "rgba(255,255,255,0.85)"; g.lineWidth = 7; g.shadowColor = "#fff"; g.shadowBlur = 14; g.stroke();
  }
  const r = g.createRadialGradient(0, 0, 0, 0, 0, c);
  r.addColorStop(0, "rgba(255,255,255,0.5)"); r.addColorStop(0.3, "rgba(255,255,255,0.08)"); r.addColorStop(1, "rgba(255,255,255,0)");
  g.fillStyle = r; g.fillRect(-c, -c, w, w);
});
const hexTex = () => canvasTex("art_hex_v1", 256, 256, (g, w) => {
  g.strokeStyle = "rgba(255,255,255,0.9)"; g.lineWidth = 2.2; g.shadowColor = "#fff"; g.shadowBlur = 6;
  const s = 18, hh = s * Math.sqrt(3);
  for (let row = -1; row < 10; row++) for (let col = -1; col < 10; col++) {
    const cx = col * s * 1.5, cy = row * hh + (col % 2 ? hh / 2 : 0);
    g.beginPath();
    for (let i = 0; i <= 6; i++) { const a = i * Math.PI / 3; const x = cx + Math.cos(a) * s, y = cy + Math.sin(a) * s; i ? g.lineTo(x, y) : g.moveTo(x, y); }
    g.stroke();
  }
  // a soft edge so the disc/dome never shows a hard rim
  const m = g.createRadialGradient(w / 2, w / 2, w * 0.3, w / 2, w / 2, w / 2);
  m.addColorStop(0, "rgba(0,0,0,0)"); m.addColorStop(1, "rgba(0,0,0,1)");
  g.globalCompositeOperation = "destination-out"; g.fillStyle = m; g.fillRect(0, 0, w, w);
});
// glowing radial fractures — the arena's own lit seams, cracked into the floor by a blow
const fractureTex = () => canvasTex("art_fracture_v1", 512, 512, (g, w) => {
  const c = w / 2, R = mulberry32(9090);
  g.lineJoin = "round"; g.lineCap = "round";
  const crack = (x, y, ang, len, width, depth) => {
    const pts = [[x, y]];
    let a = ang;
    for (let i = 0; i < 6; i++) { a += (R() - 0.5) * 0.7; x += Math.cos(a) * len / 6; y += Math.sin(a) * len / 6; pts.push([x, y]); }
    for (const [lw, al, blur] of [[width * 4, 0.18, 18], [width * 1.8, 0.45, 8], [width * 0.7, 1, 2]]) {   // halo → glow → white-hot core
      g.strokeStyle = `rgba(255,255,255,${al})`; g.lineWidth = lw; g.shadowColor = "#fff"; g.shadowBlur = blur;
      g.beginPath(); pts.forEach(([px, py], i) => (i ? g.lineTo(px, py) : g.moveTo(px, py))); g.stroke();
    }
    if (depth > 0) for (let k = 0; k < 2; k++) { const [bx, by] = pts[2 + Math.floor(R() * 3)]; crack(bx, by, a + (R() - 0.5) * 1.6, len * 0.45, width * 0.6, depth - 1); }
  };
  for (let i = 0; i < 9; i++) crack(c, c, (i / 9) * Math.PI * 2 + R() * 0.4, c * (0.7 + R() * 0.25), 3, 1);
  // fade to the rim so the disc never shows its edge
  const m = g.createRadialGradient(c, c, c * 0.55, c, c, c);
  m.addColorStop(0, "rgba(0,0,0,0)"); m.addColorStop(1, "rgba(0,0,0,1)");
  g.globalCompositeOperation = "destination-out"; g.shadowBlur = 0; g.fillStyle = m; g.fillRect(0, 0, w, w);
});
const shadowTex = () => canvasTex("art_shadow_v1", 128, 128, (g) => {
  const r = g.createRadialGradient(64, 64, 0, 64, 64, 64);
  r.addColorStop(0, "rgba(6,2,12,0.95)"); r.addColorStop(0.6, "rgba(6,2,12,0.7)"); r.addColorStop(1, "rgba(6,2,12,0)");
  g.fillStyle = r; g.fillRect(0, 0, 128, 128);
});
const pipTex = (n) => canvasTex("art_pip_v1_" + n, 128, 128, (g) => {
  g.fillStyle = "#f6efe0"; g.fillRect(0, 0, 128, 128);
  g.strokeStyle = "#2a1a10"; g.lineWidth = 8; g.strokeRect(4, 4, 120, 120);
  const P = { 1: [[64, 64]], 2: [[34, 34], [94, 94]], 3: [[30, 30], [64, 64], [98, 98]], 4: [[34, 34], [94, 34], [34, 94], [94, 94]],
    5: [[30, 30], [98, 30], [64, 64], [30, 98], [98, 98]], 6: [[34, 28], [94, 28], [34, 64], [94, 64], [34, 100], [94, 100]] }[n];
  g.fillStyle = n === 1 ? "#c41e3a" : "#1b1020";
  for (const [x, y] of P) { g.beginPath(); g.arc(x, y, n === 1 ? 16 : 11, 0, 7); g.fill(); }
});

// ── the sweeping crescent: positions built in the shader from uv ─
const SLASH_VERT = `
uniform float uArc; uniform float uWidth;
varying vec2 vUv;
void main(){
  vUv = uv;
  float a = (uv.x - 0.5) * uArc;
  float taper = pow(sin(3.14159265 * uv.x), 0.6);
  float r = 1.0 - (1.0 - uv.y) * uWidth * taper;
  vec3 p = vec3(sin(a) * r, cos(a) * r, 0.0);
  gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
}`;
const SLASH_FRAG = `
uniform vec3 uColor; uniform float uHead; uniform float uTail; uniform float uOpacity;
varying vec2 vUv;
void main(){
  float behind = uHead - vUv.x;
  if (behind < 0.0 || behind > uTail) discard;
  float a = 1.0 - behind / uTail; a = a * a;
  float head = smoothstep(0.0, 0.06, behind);          // a hair of softness right at the leading edge
  // a soft streak across the band, brightest near its outer edge, with a
  // white-hot core — a fake bloom, no post-processing needed
  float g = exp(-pow((vUv.y - 0.7) / 0.22, 2.0));
  float hot = pow(g, 5.0);
  vec3 c = mix(uColor * 1.5, vec3(1.0), hot * 0.9 * a);
  gl_FragColor = vec4(c, a * head * (g * 0.9 + hot * 0.6) * uOpacity);
}`;

// ── the dome: a fresnel shell (bright at its rim, clear in the middle,
//    like a real energy field); the ink dome is dark all through ────
const DOME_VERT = `
varying vec3 vN; varying vec3 vV;
void main(){
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  vN = normalize(normalMatrix * normal); vV = normalize(-mv.xyz);
  gl_Position = projectionMatrix * mv;
}`;
const DOME_FRAG = `
uniform vec3 uColor; uniform float uOpacity; uniform float uInk;
varying vec3 vN; varying vec3 vV;
void main(){
  float f = 1.0 - abs(dot(normalize(vN), normalize(vV)));
  float rim = pow(f, 2.2);
  if (uInk > 0.5) { gl_FragColor = vec4(uColor, (0.55 + 0.45 * rim) * uOpacity); return; }
  vec3 c = mix(uColor, vec3(1.0), pow(rim, 3.0) * 0.7);
  gl_FragColor = vec4(c * 1.3, (0.06 + 0.94 * rim) * uOpacity);
}`;
const domeMat = (ink) => new THREE.ShaderMaterial({
  uniforms: { uColor: { value: new THREE.Color(ink ? "#05020a" : "#ffffff") }, uOpacity: { value: 1 }, uInk: { value: ink ? 1 : 0 } },
  vertexShader: DOME_VERT, fragmentShader: DOME_FRAG, transparent: true, depthWrite: false,
  blending: ink ? THREE.NormalBlending : ADD, side: THREE.DoubleSide,
});

// ── the column: a beam, not a tube — bright where it faces you, soft at its
//    edges, brightest at the floor, with bands of light running up it ────
const COL_VERT = `
varying vec2 vUv; varying vec3 vN; varying vec3 vV;
void main(){
  vUv = uv;
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  vN = normalize(normalMatrix * normal); vV = normalize(-mv.xyz);
  gl_Position = projectionMatrix * mv;
}`;
const COL_FRAG = `
uniform vec3 uColor; uniform float uOpacity; uniform float uPhase;
varying vec2 vUv; varying vec3 vN; varying vec3 vV;
void main(){
  float face = pow(abs(dot(normalize(vN), normalize(vV))), 2.2);
  float up = pow(1.0 - vUv.y, 1.4) * smoothstep(1.0, 0.75, vUv.y);
  float bands = 0.8 + 0.2 * sin(vUv.y * 46.0 - uPhase);
  vec3 c = mix(uColor, vec3(1.0), pow(face, 3.0) * 0.8);
  gl_FragColor = vec4(c * 1.4, face * up * bands * uOpacity);
}`;

// ── building the pools ──────────────────────────────────────
function buildPools() {
  const root = new THREE.Group(); root.name = "artfx";
  const geos = {
    slash: new THREE.PlaneGeometry(1, 1, 48, 3),
    rock: new THREE.IcosahedronGeometry(1, 0),
    die: new THREE.BoxGeometry(1, 1, 1),
    coin: new THREE.CylinderGeometry(1, 1, 0.12, 28),
    dome: new THREE.SphereGeometry(1, 36, 18),
    column: new THREE.CylinderGeometry(1, 1, 1, 28, 1, true).translate(0, 0.5, 0),
    disc: new THREE.CircleGeometry(1, 48).rotateX(-Math.PI / 2),
    shard: new THREE.OctahedronGeometry(1, 0).scale(0.22, 1, 0.22),
    halo: new THREE.TorusGeometry(1, 0.022, 6, 64),
  };
  geos.blot = geos.disc;
  const tex = { column: columnTex(), swirl: swirlTex(), hex: hexTex(), fracture: fractureTex(), shadow: shadowTex(), glow: glowTex() };
  const pips = [1, 2, 3, 4, 5, 6].map(pipTex);
  const make = {
    slash: () => {
      const m = new THREE.Mesh(geos.slash, new THREE.ShaderMaterial({
        uniforms: { uColor: { value: new THREE.Color() }, uHead: { value: 0 }, uTail: { value: 0.5 }, uOpacity: { value: 1 }, uArc: { value: 2.4 }, uWidth: { value: 0.22 } },
        vertexShader: SLASH_VERT, fragmentShader: SLASH_FRAG, transparent: true, depthWrite: false, depthTest: false, blending: ADD, side: THREE.DoubleSide, toneMapped: false,
      }));
      m.rotation.order = "YXZ";
      return m;
    },
    rock: () => {
      // an obsidian rock burning from inside (the arena's own stone), wrapped in its glow
      const m = new THREE.Mesh(geos.rock, new THREE.MeshStandardMaterial({ color: "#1c1529", emissive: "#ffffff", emissiveIntensity: 1.1, flatShading: true, roughness: 0.3, metalness: 0.2 }));
      const halo = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex.glow, transparent: true, depthWrite: false, blending: ADD, toneMapped: false }));
      halo.scale.setScalar(3.4); m.add(halo); m.userData.halo = halo;
      return m;
    },
    die: () => { const m = new THREE.Mesh(geos.die, [0, 1, 2, 3, 4, 5].map((i) => new THREE.MeshBasicMaterial({ map: pips[[0, 5, 1, 4, 2, 3][i]], transparent: true }))); m.rotation.order = "YXZ"; return m; },   // YXZ: the face tilt is applied AFTER the yaw, so the engine's roll lands up
    coin: () => new THREE.Mesh(geos.coin, new THREE.MeshStandardMaterial({ color: "#F2C14E", metalness: 0.85, roughness: 0.28, emissive: "#7a5a10", emissiveIntensity: 0.6, transparent: true })),
    dome: () => new THREE.Mesh(geos.dome, domeMat(false)),
    column: () => new THREE.Mesh(geos.column, new THREE.ShaderMaterial({
      uniforms: { uColor: { value: new THREE.Color() }, uOpacity: { value: 1 }, uPhase: { value: 0 } },
      vertexShader: COL_VERT, fragmentShader: COL_FRAG, transparent: true, depthWrite: false, blending: ADD, side: THREE.FrontSide })),
    disc: () => new THREE.Mesh(geos.disc, new THREE.MeshBasicMaterial({ map: tex.swirl, color: "#fff", transparent: true, depthWrite: false, blending: ADD, toneMapped: false })),
    blot: () => new THREE.Mesh(geos.blot, new THREE.MeshBasicMaterial({ map: tex.shadow, transparent: true, depthWrite: false })),
    // a CRYSTAL, like the arena's crown: dark faceted obsidian lit from inside, with a glow shell
    shard: () => {
      const m = new THREE.Mesh(geos.shard, new THREE.MeshStandardMaterial({ color: "#1a1326", emissive: "#ffffff", emissiveIntensity: 1.3, flatShading: true, roughness: 0.22, metalness: 0.25, transparent: true }));
      const rim = new THREE.Mesh(geos.shard, new THREE.MeshBasicMaterial({ color: "#fff", transparent: true, depthWrite: false, blending: ADD, side: THREE.BackSide, toneMapped: false, opacity: 0.55 }));
      rim.scale.setScalar(1.35); m.add(rim); m.userData.rim = rim;
      return m;
    },
    halo: () => new THREE.Mesh(geos.halo, new THREE.MeshBasicMaterial({ color: "#fff", transparent: true, depthWrite: false, blending: ADD, toneMapped: false })),
  };
  // a dark dome is a second material on the same slot type (normal blending, ink)
  const pools = {};
  for (const [type, n] of Object.entries(POOLS)) {
    pools[type] = { next: 0, slots: [] };
    for (let i = 0; i < n; i++) {
      const mesh = make[type]();
      mesh.visible = false; mesh.frustumCulled = type !== "slash"; mesh.renderOrder = type === "blot" ? 2 : type === "slash" ? 8 : 5;   // slashes draw OVER the bodies (anime cuts read through whoever throws them)
      if (type === "dome") mesh.userData.ink = domeMat(true);
      if (type === "dome") mesh.userData.lit = mesh.material;
      root.add(mesh);
      pools[type].slots.push({ mesh, on: false, t: 0, life: 1, upd: null, end: null, d: {} });
    }
  }
  const dispose = () => {
    Object.values(geos).forEach((g) => g.dispose());
    root.traverse((o) => {
      if (o.userData.lit) { o.userData.lit.dispose(); o.userData.ink.dispose(); return; }   // a dome owns two materials, whichever it wears
      if (!o.material) return;
      (Array.isArray(o.material) ? o.material : [o.material]).forEach((m) => m.dispose());
    });
  };
  return { root, pools, list: Object.values(pools), tex, dispose };
}

const ease3 = (k) => 1 - Math.pow(1 - k, 3);
const V = new THREE.Vector3();
const CAM = { x: 0, z: 14 };          // where the viewer stands this frame (the Four always face them)

export default function ArtFx({ fx }) {
  const P = useMemo(buildPools, []);
  const timers = useMemo(() => Array.from({ length: N_TIMERS }, () => ({ on: false, t: 0, fn: null })), []);
  useEffect(() => () => P.dispose(), [P]);
  useEffect(() => { if (process.env.NODE_ENV !== "production" && typeof window !== "undefined") window.__artFx = P; }, [P]);   // dev console handle: the pools

  useEffect(() => {
    const take = (type, life, upd, end) => {
      const pool = P.pools[type];
      const s = pool.slots[pool.next]; pool.next = (pool.next + 1) % pool.slots.length;
      if (s.on && s.end) { const e = s.end; s.end = null; e(V.copy(s.mesh.position)); }   // a recycled slot still finishes its job (same argument as a normal end: a SHARED vector — copy it if you keep it)
      s.on = true; s.t = 0; s.life = Math.max(0.05, life); s.upd = upd; s.end = end || null;
      s.mesh.visible = true;
      return s;
    };
    fx.after = (sec, fn) => {
      const t = timers.find((x) => !x.on);
      if (!t) { fn(); return; }                    // pool full: do it now rather than drop it
      t.on = true; t.t = sec; t.fn = fn;
    };
    fx.slash = (o) => {
      const s = take("slash", o.life || 0.45, (m, k, d) => {
        const sweep = d.sweep;
        const u = m.material.uniforms;
        // the head runs the arc, then STOPS at its end: the tail stays and fades in place (the cut lingers)
        u.uHead.value = Math.min(1, k / sweep);
        u.uOpacity.value = k < sweep ? 1 : Math.max(0, 1 - (k - sweep) / (1 - sweep));
      });
      const m = s.mesh, u = m.material.uniforms;
      Object.assign(s.d, { sweep: o.sweep || 0.55, tail: o.tail || 0.5 });
      u.uColor.value.set(o.color || "#fff"); u.uTail.value = o.tail || 0.5; u.uArc.value = o.arc || 2.4; u.uWidth.value = o.width || 0.22;
      u.uHead.value = 0; u.uOpacity.value = 1;
      m.position.set(o.x || 0, o.y || 1, o.z || 0);
      m.rotation.set(o.pitch || 0, o.yaw || 0, o.roll || 0);
      const r = o.r || 1; m.scale.set(o.flip ? -r : r, r, r);
    };
    fx.rock = (o) => {
      const [x0, y0, z0] = o.from, [x1, y1, z1] = o.to;
      const s = take("rock", o.life || 0.45, (m, k, d, dt) => {
        const e = k * k;                                     // it falls faster as it comes
        m.position.set(x0 + (x1 - x0) * e, y0 + (y1 - y0) * e, z0 + (z1 - z0) * e);
        m.rotation.x += dt * 7; m.rotation.y += dt * 5;
        d.acc += dt;
        if (fx.burst && d.acc > 0.02) { d.acc = 0; fx.burst(m.position.x, m.position.y, m.position.z, d.trail, 2, 1.2, d.tOpt); }
      }, o.onEnd);
      s.d.acc = 0; s.d.trail = o.trail || o.color || "#ff9b2e"; s.d.r = o.r || 0.5;
      s.d.tOpt = s.d.tOpt || {}; Object.assign(s.d.tOpt, { life: 0.45, size: 0.6 * s.d.r, drag: 3, white: 0.2 });   // one options object per slot, not per spark
      s.mesh.material.emissive.set(o.core || o.color || "#ff9b2e"); s.mesh.userData.halo.material.color.set(o.color || "#ff9b2e");
      s.mesh.scale.setScalar(o.r || 0.5); s.mesh.position.set(x0, y0, z0);
    };
    fx.dome = (o) => {
      const s = take("dome", o.life || 0.8, (m, k, d) => {
        const e = d.implode ? k * k : ease3(Math.min(1, k * 1.6));   // implode: r0 (big) → r1 (small), faster as it falls in
        const r = d.r0 + (d.r1 - d.r0) * e;
        m.scale.set(r, r * d.squash, r);
        m.material.uniforms.uOpacity.value = d.op * (k < 0.6 ? 1 : 1 - (k - 0.6) / 0.4);
      });
      const m = s.mesh;
      m.material = o.dark ? m.userData.ink : m.userData.lit;
      if (!o.dark) m.material.uniforms.uColor.value.set(o.color || "#fff");
      Object.assign(s.d, { r0: o.r0 ?? 0.2, r1: o.r1 ?? 3, op: o.opacity ?? (o.dark ? 0.85 : 0.6), squash: o.squash ?? 0.75, implode: !!o.implode });
      m.position.set(o.x || 0, o.y || 0, o.z || 0); m.scale.setScalar(0.01);
    };
    fx.column = (o) => {
      const s = take("column", o.life || 0.9, (m, k, d) => {
        const grow = ease3(Math.min(1, k * 4)), fade = k < 0.5 ? 1 : 1 - (k - 0.5) / 0.5;
        m.scale.set(d.r * (0.4 + 0.6 * grow) * (1 + 0.1 * Math.sin(k * 40)), d.h * grow, d.r * (0.4 + 0.6 * grow));
        m.material.uniforms.uOpacity.value = fade * d.op;
        m.material.uniforms.uPhase.value = k * 26;
      });
      s.mesh.material.uniforms.uColor.value.set(o.color || "#fff");
      Object.assign(s.d, { r: o.r || 1.2, h: o.h || 16, op: o.opacity ?? 0.7 });
      s.mesh.position.set(o.x || 0, 0, o.z || 0); s.mesh.scale.setScalar(0.01);
    };
    fx.disc = (o) => {
      const s = take("disc", o.life || 1, (m, k, d, dt) => {
        const r = d.r0 + (d.r1 - d.r0) * ease3(Math.min(1, k * 2));
        m.scale.set(r, 1, r);
        if (d.face) m.rotation.y = Math.atan2(CAM.x - m.position.x, CAM.z - m.position.z);   // the Four: Khao at the top of YOUR screen
        else m.rotation.y += dt * d.spin;
        m.material.opacity = d.op * (k < 0.15 ? k / 0.15 : k > 0.7 ? 1 - (k - 0.7) / 0.3 : 1);
      });
      const m = s.mesh; m.material.map = typeof o.tex === "object" ? o.tex : P.tex[o.tex || "swirl"]; m.material.color.set(o.color || "#fff");
      Object.assign(s.d, { r0: o.r0 ?? 0.3, r1: o.r1 ?? 2.5, spin: o.spin ?? 2, op: o.opacity ?? 0.9, face: !!o.face });
      m.position.set(o.x || 0, o.y || 0.04, o.z || 0); m.rotation.set(0, o.yaw || 0, 0); m.scale.setScalar(0.01);
    };
    // an Art's SEAL (data/seals.js): its rim + heart in the caster's colour, turning;
    // the Four in their own colours, still, facing the viewer. Two discs, one seal.
    fx.seal = (o) => {
      const seal = sealOf(o.art);
      const base = { x: o.x, z: o.z, y: o.y ?? 0.04, r0: o.r0, r1: o.r1, life: o.life, opacity: o.opacity };
      fx.disc({ ...base, tex: sealBodyTex(seal), color: o.color || "#fff", spin: o.spin ?? 1.2 });
      fx.disc({ ...base, y: base.y + 0.004, tex: sealFourTex(seal), color: "#fff", face: true, opacity: Math.min(1, (o.opacity ?? 0.9) * 1.3) });
    };
    // the floor cracks open and glows, the way the arena's seams do (a disc with the fracture glyph)
    fx.fracture = (o) => fx.disc({ x: o.x, z: o.z, y: 0.035, r0: (o.r || 2) * 0.7, r1: o.r || 2, color: o.color || "#fff", tex: "fracture", spin: 0, yaw: o.yaw ?? (o.x * 3.1 + o.z * 1.7), life: o.life || 1.3, opacity: o.opacity ?? 1 });
    fx.blot = (o) => {
      const s = take("blot", o.life || 0.8, (m, k, d) => {
        const r = d.r0 + (d.r1 - d.r0) * ease3(k);
        m.scale.set(r, 1, r);
        m.material.opacity = d.op * (k > 0.85 ? (1 - k) / 0.15 : 1);
      });
      Object.assign(s.d, { r0: o.r0 ?? 0.3, r1: o.r1 ?? 3, op: o.opacity ?? 0.9 });
      s.mesh.position.set(o.x || 0, 0.03, o.z || 0); s.mesh.scale.setScalar(0.01);
    };
    fx.shard = (o) => {
      const s = take("shard", o.life || 0.8, (m, k, d, dt) => {
        m.position.x += d.vx * dt; m.position.y += d.vy * dt; m.position.z += d.vz * dt;
        d.vy -= d.g * dt;
        m.rotation.y += d.spin * dt;
        const pop = Math.min(1, k * 6);
        m.scale.set(d.len * pop, d.len * pop, d.len * pop);
        const f = k > 0.7 ? (1 - k) / 0.3 : 1;
        m.material.opacity = f; m.userData.rim.material.opacity = 0.55 * f;
      });
      s.mesh.material.emissive.set(o.color || "#fff"); s.mesh.userData.rim.material.color.set(o.color || "#fff");
      Object.assign(s.d, { vx: o.vx || 0, vy: o.vy || 0, vz: o.vz || 0, g: o.g || 0, spin: o.spin ?? 3, len: o.len || 0.8 });
      s.mesh.position.set(o.x || 0, o.y || 0, o.z || 0); s.mesh.rotation.set(o.tilt || 0, o.yaw || 0, o.roll || 0); s.mesh.scale.setScalar(0.01);
    };
    fx.halo = (o) => {
      const s = take("halo", o.life || 0.9, (m, k, d, dt) => {
        const r = d.r0 + (d.r1 - d.r0) * ease3(k);
        m.scale.setScalar(r);
        m.position.y += d.vy * dt;
        m.material.opacity = k > 0.6 ? (1 - k) / 0.4 : 1;
      });
      s.mesh.material.color.set(o.color || "#fff");
      Object.assign(s.d, { r0: o.r0 ?? 0.3, r1: o.r1 ?? 1.2, vy: o.vy || 0 });
      s.mesh.position.set(o.x || 0, o.y || 1, o.z || 0);
      s.mesh.rotation.set(o.tilt != null ? o.tilt : Math.PI / 2, o.yaw || 0, 0); s.mesh.scale.setScalar(0.01);
    };
    fx.die = (o) => {
      const s = take("die", o.life || 1.6, (m, k, d, dt) => {
        // falls, bounces twice, settles on its face, then fades
        const land = 0.35;
        const y = k < land ? 7 * (1 - k / land) * (1 - k / land) + 0.6 : 0.6 + Math.abs(Math.sin((k - land) * 14)) * 0.9 * Math.max(0, 1 - (k - land) * 3);
        m.position.y = y;
        const spin = Math.max(0, 1 - k * 1.6);
        m.rotation.x += dt * 9 * spin; m.rotation.z += dt * 7 * spin;
        if (spin === 0 && !d.set) { d.set = true; m.rotation.set(d.face[0], m.rotation.y, d.face[1]); }
        if (k > land && !d.hit) { d.hit = true; if (d.onLand) d.onLand(m.position.x, m.position.z); }
        const fade = k > 0.85 ? (1 - k) / 0.15 : 1;
        for (const mat of m.material) mat.opacity = fade;
      });
      // which way up shows which pip (box faces: +x 1, −x 6, +y 2, −y 5, +z 3, −z 4)
      const FACE = { 1: [0, Math.PI / 2], 2: [0, 0], 3: [-Math.PI / 2, 0], 4: [Math.PI / 2, 0], 5: [Math.PI, 0], 6: [0, -Math.PI / 2] };
      Object.assign(s.d, { face: FACE[o.face || 6], set: false, hit: false, onLand: o.onLand || null });
      s.mesh.scale.setScalar(o.size || 1.1); s.mesh.position.set(o.x || 0, 7, o.z || 0); s.mesh.rotation.set(0, o.yaw || 0, 0);
    };
    fx.coin = (o) => {
      const s = take("coin", o.life || 1.4, (m, k, d, dt) => {
        const up = Math.sin(Math.min(1, k / 0.75) * Math.PI);  // flipped up, caught on the way down
        m.position.y = d.y + up * 2.2;
        m.rotation.x += dt * (k < 0.75 ? 22 : 0);
        if (k >= 0.75 && !d.set) { d.set = true; m.rotation.x = d.heads ? 0 : Math.PI; if (d.onLand) d.onLand(); }
        m.material.opacity = k > 0.85 ? (1 - k) / 0.15 : 1;
      });
      Object.assign(s.d, { y: o.y || 2.4, heads: o.heads !== false, set: false, onLand: o.onLand || null });
      s.mesh.scale.setScalar(o.size || 0.45); s.mesh.position.set(o.x || 0, o.y || 2.4, o.z || 0); s.mesh.rotation.set(0, o.yaw || 0, 0);
    };
  }, [fx, P, timers]);

  useFrame(({ camera }, dtRaw) => {
    const dt = Math.min(dtRaw, 0.05) * (fx.timeScale ?? 1);
    CAM.x = camera.position.x; CAM.z = camera.position.z;
    for (const t of timers) {
      if (!t.on) continue;
      t.t -= dt;
      if (t.t <= 0) { t.on = false; const fn = t.fn; t.fn = null; fn(); }
    }
    for (const pool of P.list) {
      for (const s of pool.slots) {
        if (!s.on) continue;
        s.t += dt;
        const k = Math.min(1, s.t / s.life);
        if (s.upd) s.upd(s.mesh, k, s.d, dt);
        if (k >= 1) {
          s.on = false; s.mesh.visible = false;
          if (s.end) { const e = s.end; s.end = null; e(V.copy(s.mesh.position)); }
        }
      }
    }
  });

  return <primitive object={P.root} />;
}
